/**
 * THE PRICE AND VOLUME SERIES, AS A PATCH — one definition, two callers.
 *
 * <h2>Why this is a module and not two copies</h2>
 *
 * `EChartComponent` has patched the price series by id since study mode shipped: a partial
 * `setOption` carrying `__candle__` and `__volume__`, which updates those two series and leaves the
 * other thirty alone. The replay needs exactly the same thing to unveil bars, and a second copy of
 * it would be a second place to get the type wrong.
 *
 * <p>That is not a hypothetical. The study patch originally hardcoded `'candlestick'`, which
 * silently converted a LINE chart into candles mid-run — and with the hidden view it also undid the
 * hiding, because a candlestick series carries none of the zero-opacity styling. The comment
 * recording that fix is the reason this function takes the chart type as an argument.
 *
 * <h2>THE PATCH MUST RESTATE THE TYPE AND THE STYLING</h2>
 *
 * An ECharts merge by id replaces what it names and keeps what it omits — but a series' TYPE is
 * not something it is safe to omit, because the patch is also what re-declares the series if the
 * option has been rebuilt underneath it. So each shape is stated in full, for all three views.
 */

/** The ids `chartDataProcessor` stamps on the two series, and the only way to patch them. */
export const CANDLE_SERIES_ID = '__candle__';
export const VOLUME_SERIES_ID = '__volume__';

/**
 * The closing price of a bar, or null.
 *
 * <p>Null rather than 0 for a missing bar: a line series draws a gap for null and a plunge to zero
 * for 0, and the second is a price the market never printed.
 */
const closeOf = (bar) => (bar && bar[1] != null ? bar[1] : null);

/**
 * The two series, shaped for a partial `setOption`.
 *
 * @param chartType 'candlestick' | 'line' | 'hidden' — the view the reader is on
 * @param ohlc      the price array, already truncated by whatever is doing the truncating
 * @param volumes   the volume array, likewise. Omitted leaves the volume series alone
 * @returns an array of one or two series objects
 */
export function pricePatchSeries(chartType, ohlc, volumes) {
  const bars = Array.isArray(ohlc) ? ohlc : [];

  const price = chartType === 'candlestick'
    ? {
      id: CANDLE_SERIES_ID,
      type: 'candlestick',
      xAxisIndex: 0,
      yAxisIndex: 0,
      data: bars,
      // ANIMATION OFF. A candle that eases into place is a candle arriving late, and during a
      // replay the whole point is that the bar appears on the tick it belongs to.
      animation: false,
    }
    : {
      id: CANDLE_SERIES_ID,
      type: 'line',
      xAxisIndex: 0,
      yAxisIndex: 0,
      animation: false,
      data: bars.map(closeOf),
      // THE HIDDEN VIEW'S STYLING IS PART OF THE PATCH. Without it, patching the series would
      // restore a price line the reader has deliberately turned off — the second half of the
      // defect the hardcoded type caused.
      ...(chartType === 'hidden'
        ? {
          lineStyle: { opacity: 0, width: 0 },
          itemStyle: { opacity: 0 },
          showSymbol: false,
          silent: true,
        }
        : {}),
    };

  if (!Array.isArray(volumes)) return [price];

  return [
    price,
    {
      id: VOLUME_SERIES_ID,
      type: 'bar',
      xAxisIndex: 1,
      yAxisIndex: 2,
      data: volumes,
      animation: false,
    },
  ];
}
